import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  Calendar,
  CalendarDays,
  Check,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  Clock,
  Copy,
  FileText,
  FolderKanban,
  Lightbulb,
  Link2,
  Loader2,
  Package,
  Pencil,
  Pin,
  Search,
  Send,
  Trash2,
  User as UserIcon,
  X,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ReferenceIdBadge } from "@/components/ui/ReferenceIdBadge";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { TagBadge } from "@/components/ui/TagBadge";
import { VoteButtons } from "@/components/ui/VoteButtons";
import { AssociationFields } from "@/components/shared/AssociationFields";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { FileUploadZone } from "@/modules/media/components/FileUploadZone";
import { TasksTabContent } from "@/modules/tasks/components/TasksTabContent";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import {
  useAddIdeaComment,
  useCreateIdea,
  useDeleteIdea,
  useDeleteIdeaComment,
  useIdea,
  useIdeaCategories,
  useIdeaComments,
  useIdeaPriorities,
  useIdeaStatuses,
  useIdeaTags,
  useLinkDecision,
  useRemoveIdeaVote,
  useToggleIdeaPin,
  useUnlinkDecision,
  useUpdateIdea,
  useVoteIdea,
} from "../hooks/useIdeas";

const BORDER = "hsl(210,30%,85%)";

function stripHtml(html: string): string {
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function countWords(html: string): number {
  const text = stripHtml(html);
  if (!text) return 0;
  return text.split(/\s+/).filter(Boolean).length;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatRelative(iso: string): string {
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

const STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  captured: { bg: "hsl(215,15%,94%)", text: "hsl(215,20%,38%)", border: "hsl(215,12%,82%)" },
  exploring: { bg: "hsl(210,85%,95%)", text: "hsl(210,85%,38%)", border: "hsl(210,75%,78%)" },
  adopted: { bg: "hsl(142,55%,94%)", text: "hsl(142,65%,30%)", border: "hsl(142,45%,78%)" },
  rejected: { bg: "hsl(0,70%,94%)", text: "hsl(0,70%,38%)", border: "hsl(0,70%,82%)" },
  on_hold: { bg: "hsl(40,95%,94%)", text: "hsl(35,85%,38%)", border: "hsl(40,85%,80%)" },
};

const CATEGORY_COLORS: Record<string, { bg: string; text: string }> = {
  feature: { bg: "hsl(210,85%,95%)", text: "hsl(210,85%,38%)" },
  improvement: { bg: "hsl(180,60%,93%)", text: "hsl(180,70%,28%)" },
  bug: { bg: "hsl(0,70%,94%)", text: "hsl(0,70%,38%)" },
  research: { bg: "hsl(270,70%,96%)", text: "hsl(270,60%,42%)" },
  process: { bg: "hsl(142,55%,94%)", text: "hsl(142,65%,30%)" },
  other: { bg: "hsl(215,15%,94%)", text: "hsl(215,20%,38%)" },
};

const PRIORITY_COLORS: Record<string, { bg: string; text: string }> = {
  low: { bg: "hsl(215,15%,94%)", text: "hsl(215,20%,38%)" },
  medium: { bg: "hsl(210,85%,95%)", text: "hsl(210,85%,38%)" },
  high: { bg: "hsl(35,85%,94%)", text: "hsl(35,85%,38%)" },
  critical: { bg: "hsl(0,70%,94%)", text: "hsl(0,70%,38%)" },
};

function StatusBadge({ value, label }: { value: string; label: string }) {
  const c = STATUS_COLORS[value] ?? STATUS_COLORS.captured;
  return (
    <span
      className="inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium"
      style={{ background: c.bg, color: c.text, borderColor: c.border }}
    >
      {label}
    </span>
  );
}

function CategoryBadge({ value, label }: { value: string; label: string }) {
  const c = CATEGORY_COLORS[value] ?? CATEGORY_COLORS.other;
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
      style={{ background: c.bg, color: c.text }}
    >
      {label}
    </span>
  );
}

function PriorityBadge({ value, label }: { value: string; label: string }) {
  const c = PRIORITY_COLORS[value] ?? PRIORITY_COLORS.medium;
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
      style={{ background: c.bg, color: c.text }}
    >
      {label}
    </span>
  );
}

function IconBtn({
  icon: Icon,
  label,
  onClick,
  active,
  color,
  disabled,
}: {
  icon: typeof Pin;
  label: string;
  onClick: () => void;
  active?: boolean;
  color?: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className="inline-flex h-8 w-8 items-center justify-center rounded-md border bg-white text-muted-foreground transition-colors hover:bg-[hsl(210,50%,96%)] hover:text-foreground disabled:opacity-50"
      style={{ borderColor: BORDER }}
    >
      <Icon
        className="h-[15px] w-[15px]"
        strokeWidth={1.5}
        style={{
          color: active && color ? color : undefined,
          fill: active && color ? color : "none",
        }}
      />
    </button>
  );
}

type Heading = { id: string; level: number; text: string };

function extractHeadings(html: string): Heading[] {
  if (!html) return [];
  const div = document.createElement("div");
  div.innerHTML = html;
  const nodes = Array.from(div.querySelectorAll("h1,h2,h3"));
  return nodes.map((el, i) => ({
    id: `heading-${i}`,
    level: parseInt(el.tagName.substring(1), 10),
    text: el.textContent ?? "",
  }));
}

type DecisionLite = {
  id: string;
  code: string | null;
  title: string;
  status: string;
  impact: string;
  decidedAt: string | null;
  decidedByName: string | null;
};

export default function IdeaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const { user, accessToken } = useAuth();
  const ideaId = id ?? "";

  const { data: idea, isLoading, isError } = useIdea(ideaId);
  const { data: comments = [] } = useIdeaComments(ideaId);

  const updateIdea = useUpdateIdea(ideaId);
  const togglePin = useToggleIdeaPin();
  const deleteIdea = useDeleteIdea();
  const createIdea = useCreateIdea();
  const addComment = useAddIdeaComment(ideaId);
  const deleteComment = useDeleteIdeaComment(ideaId);
  const vote = useVoteIdea(ideaId);
  const removeVote = useRemoveIdeaVote(ideaId);
  const linkDecision = useLinkDecision(ideaId);
  const unlinkDecision = useUnlinkDecision(ideaId);

  const categories = useIdeaCategories().data ?? [];
  const priorities = useIdeaPriorities().data ?? [];
  const statuses = useIdeaStatuses().data ?? [];
  const tagOptions = useIdeaTags().data ?? [];

  const [editingMeta, setEditingMeta] = useState(false);
  const [editingContent, setEditingContent] = useState(
    search.get("edit") === "true",
  );
  const [titleDraft, setTitleDraft] = useState("");
  const [categoryDraft, setCategoryDraft] = useState("");
  const [priorityDraft, setPriorityDraft] = useState("medium");
  const [statusDraft, setStatusDraft] = useState("captured");
  const [visibilityDraft, setVisibilityDraft] = useState<"team" | "private">("team");
  const [tagsDraft, setTagsDraft] = useState<string[]>([]);
  const [isPinnedDraft, setIsPinnedDraft] = useState(false);
  const [companyIdDraft, setCompanyIdDraft] = useState<string | null>(null);
  const [productIdDraft, setProductIdDraft] = useState<string | null>(null);
  const [projectIdDraft, setProjectIdDraft] = useState<string | null>(null);
  const [meetingIdDraft, setMeetingIdDraft] = useState<string | null>(null);
  const [taskIdDraft, setTaskIdDraft] = useState<string | null>(null);
  const [contentDraft, setContentDraft] = useState("");
  const [tab, setTab] = useState<
    "decisions" | "tasks" | "meetings" | "comments" | "attachments"
  >("decisions");
  const [metaExpanded, setMetaExpanded] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [toast, setToast] = useState<string | null>(null);
  const [linkPickerOpen, setLinkPickerOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [unlinkTarget, setUnlinkTarget] = useState<{ id: string; title: string } | null>(null);

  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialContent = useRef<string>("");
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">("idle");

  useEffect(() => {
    if (idea) {
      setTitleDraft(idea.title);
      setCategoryDraft(idea.category ?? "");
      setPriorityDraft(idea.priority);
      setStatusDraft(idea.status);
      setVisibilityDraft((idea.visibility as "team" | "private") ?? "team");
      setTagsDraft(idea.tags ?? []);
      setIsPinnedDraft(idea.isPinned);
      setCompanyIdDraft(idea.companyId ?? null);
      setProductIdDraft(idea.productId ?? null);
      setProjectIdDraft(idea.projectId ?? null);
      setMeetingIdDraft(idea.meetingId ?? null);
      setTaskIdDraft(idea.taskId ?? null);
      setContentDraft(idea.content ?? "");
      initialContent.current = idea.content ?? "";
    }
  }, [idea?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const wordCount = useMemo(() => countWords(contentDraft), [contentDraft]);
  const readingMinutes = Math.max(1, Math.ceil((idea?.wordCount ?? wordCount) / 220));

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  }

  useEffect(() => {
    if (!editingContent || !idea) return;
    if (contentDraft === initialContent.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSaveState("idle");
    saveTimer.current = setTimeout(() => {
      void doContentSave();
    }, 2000);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [contentDraft, editingContent]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s" && editingContent) {
        e.preventDefault();
        void doContentSave();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editingContent, contentDraft]); // eslint-disable-line react-hooks/exhaustive-deps

  async function doContentSave() {
    if (!idea) return;
    setSaveState("saving");
    try {
      await updateIdea.mutateAsync({ content: contentDraft, wordCount });
      initialContent.current = contentDraft;
      setSaveState("saved");
    } catch {
      setSaveState("idle");
    }
  }

  async function handleMetaSave() {
    if (!idea) return;
    try {
      await updateIdea.mutateAsync({
        title: titleDraft.trim() || idea.title,
        category: categoryDraft || null,
        priority: priorityDraft,
        status: statusDraft,
        visibility: visibilityDraft,
        tags: tagsDraft,
        isPinned: isPinnedDraft,
        companyId: companyIdDraft,
        productId: productIdDraft,
        projectId: projectIdDraft,
        meetingId: meetingIdDraft,
        taskId: taskIdDraft,
      });
      setEditingMeta(false);
      showToast("Idea updated");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to save");
    }
  }

  async function handleTogglePin() {
    if (!idea) return;
    await togglePin.mutateAsync({ id: idea.id, isPinned: !idea.isPinned });
    showToast(idea.isPinned ? "Unpinned" : "Pinned");
  }

  function handleDelete() {
    if (!idea) return;
    setDeleteOpen(true);
  }

  async function confirmDelete() {
    if (!idea) return;
    await deleteIdea.mutateAsync(idea.id);
    navigate("/ideas");
  }

  async function handleDuplicate() {
    if (!idea) return;
    const res = await createIdea.mutateAsync({
      title: `${idea.title} (Copy)`,
      content: idea.content,
      category: idea.category,
      priority: idea.priority,
      status: "captured",
      tags: idea.tags,
      visibility: idea.visibility,
      companyId: idea.companyId,
      productId: idea.productId,
      projectId: idea.projectId,
      meetingId: idea.meetingId,
      taskId: idea.taskId,
    });
    showToast("Idea duplicated");
    navigate(`/ideas/${res.idea.id}`);
  }

  async function handleVote(type: "up" | "down") {
    if (!idea) return;
    try {
      if (idea.currentUserVote === type) {
        await removeVote.mutateAsync();
      } else {
        await vote.mutateAsync(type);
      }
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Vote failed");
    }
  }

  async function handleAddComment() {
    const text = commentText.trim();
    if (!text) return;
    setCommentText("");
    try {
      await addComment.mutateAsync(text);
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to add comment");
    }
  }

  const headings = useMemo(
    () =>
      extractHeadings(
        editingContent ? contentDraft : idea?.content ?? "",
      ),
    [idea?.content, contentDraft, editingContent],
  );

  if (isLoading) {
    return (
      <AppLayout breadcrumb={[{ label: "Ideas", href: "/ideas" }]}>
        <div className="py-16 text-center text-sm text-muted-foreground">Loading idea…</div>
      </AppLayout>
    );
  }
  if (isError || !idea) {
    return (
      <AppLayout breadcrumb={[{ label: "Ideas", href: "/ideas" }]}>
        <div className="py-16 text-center text-sm text-destructive">Idea not found.</div>
      </AppLayout>
    );
  }

  const categoryLabel = idea.category
    ? categories.find((c) => c.value === idea.category)?.label ?? idea.category
    : null;
  const priorityLabel =
    priorities.find((p) => p.value === idea.priority)?.label ?? idea.priority;
  const statusLabel =
    statuses.find((s) => s.value === idea.status)?.label ?? idea.status;

  const net = idea.votesUp - idea.votesDown;
  const linkedDecisions = idea.linkedDecisions ?? [];

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Planning" },
        { label: "Ideas", href: "/ideas" },
        { label: idea.code ?? idea.title },
      ]}
    >
      {toast && (
        <div
          className="fixed right-6 top-16 z-[9999] rounded-lg border bg-white px-4 py-3 text-sm font-medium text-green-700 shadow-lg"
          style={{ borderColor: "hsl(142,55%,80%)" }}
        >
          {toast}
        </div>
      )}

      <div className="w-full space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/ideas")}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border bg-white text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
              style={{ borderColor: BORDER }}
              title="Back"
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            </button>
            <Lightbulb className="h-[18px] w-[18px]" strokeWidth={1.5} />
            <h1 className="text-xl font-semibold">View Idea</h1>
          </div>
          <div className="flex items-center gap-1.5">
            <IconBtn
              icon={Pin}
              label={idea.isPinned ? "Unpin" : "Pin"}
              onClick={handleTogglePin}
              active={idea.isPinned}
              color="hsl(40,95%,55%)"
            />
            <IconBtn icon={Copy} label="Duplicate" onClick={handleDuplicate} />
            <IconBtn icon={Clock} label="History" onClick={() => showToast("History coming soon")} />
            <IconBtn icon={Pencil} label="Edit" onClick={() => setEditingMeta((v) => !v)} active={editingMeta} />
            <IconBtn icon={Trash2} label="Delete" onClick={handleDelete} />
          </div>
        </div>

        {/* ROW 1 — Summary header card */}
        <div
          className="rounded-xl border bg-white p-5"
          style={{ borderColor: BORDER }}
        >
          {editingMeta ? (
            <input
              className="w-full text-[20px] font-bold outline-none border-b pb-1"
              style={{ borderColor: BORDER }}
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
            />
          ) : (
            <h2 className="text-[20px] font-bold leading-tight">{idea.title}</h2>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2 text-[12px] text-muted-foreground">
            {idea.code && <ReferenceIdBadge id={idea.code} />}
            {editingMeta ? (
              <>
                <select
                  value={statusDraft}
                  onChange={(e) => setStatusDraft(e.target.value)}
                  className="h-7 rounded-md border px-2 text-[12px]"
                  style={{ borderColor: BORDER }}
                >
                  {statuses.length === 0 ? (
                    <>
                      <option value="captured">Captured</option>
                      <option value="exploring">Exploring</option>
                      <option value="adopted">Adopted</option>
                      <option value="rejected">Rejected</option>
                      <option value="on_hold">On Hold</option>
                    </>
                  ) : (
                    statuses.map((s) => (
                      <option key={s.id} value={s.value}>{s.label}</option>
                    ))
                  )}
                </select>
                <select
                  value={categoryDraft}
                  onChange={(e) => setCategoryDraft(e.target.value)}
                  className="h-7 rounded-md border px-2 text-[12px]"
                  style={{ borderColor: BORDER }}
                >
                  <option value="">No category</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.value}>{c.label}</option>
                  ))}
                </select>
                <select
                  value={priorityDraft}
                  onChange={(e) => setPriorityDraft(e.target.value)}
                  className="h-7 rounded-md border px-2 text-[12px]"
                  style={{ borderColor: BORDER }}
                >
                  {priorities.length === 0 ? (
                    <>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </>
                  ) : (
                    priorities.map((p) => (
                      <option key={p.id} value={p.value}>{p.label}</option>
                    ))
                  )}
                </select>
                <div className="inline-flex rounded-md border p-0.5" style={{ borderColor: BORDER }}>
                  {(["team", "private"] as const).map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setVisibilityDraft(v)}
                      className="px-2 py-0.5 text-[11px] font-medium rounded"
                      style={{
                        background: visibilityDraft === v ? "hsl(210,55%,88%)" : "transparent",
                        color: visibilityDraft === v ? "hsl(210,100%,25%)" : "hsl(215,16%,47%)",
                      }}
                    >
                      {v === "team" ? "Team" : "Private"}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <StatusBadge value={idea.status} label={statusLabel} />
                {categoryLabel && <CategoryBadge value={idea.category ?? "other"} label={categoryLabel} />}
                <PriorityBadge value={idea.priority} label={priorityLabel} />
                <span
                  className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
                  style={{
                    background: idea.visibility === "private" ? "hsl(215,15%,94%)" : "hsl(210,85%,95%)",
                    color: idea.visibility === "private" ? "hsl(215,20%,38%)" : "hsl(210,85%,38%)",
                  }}
                >
                  {idea.visibility === "private" ? "Private" : "Team"}
                </span>
                {idea.isPinned && (
                  <span
                    className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium"
                    style={{ background: "hsl(40,95%,94%)", color: "hsl(35,85%,38%)", borderColor: "hsl(40,85%,80%)" }}
                  >
                    <Pin className="h-3 w-3" strokeWidth={1.5} style={{ fill: "hsl(40,95%,55%)" }} />
                    Pinned
                  </span>
                )}
              </>
            )}
            <span className="inline-flex items-center gap-1">
              <UserIcon className="h-3.5 w-3.5" strokeWidth={1.5} />
              {idea.submittedByName ?? "—"}
            </span>
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" strokeWidth={1.5} />
              {formatDate(idea.createdAt)}
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" strokeWidth={1.5} />
              {readingMinutes} min read
            </span>
          </div>

          {/* Context breadcrumb */}
          <div className="mt-3 flex flex-wrap items-center gap-1 text-[12px] text-muted-foreground">
            {idea.companyName && (
              <>
                <Link to={`/companies/${idea.companyId}`} className="inline-flex items-center gap-1 hover:text-foreground">
                  <Building2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {idea.companyName}
                </Link>
                {idea.productName && <ChevronRight className="h-3 w-3 text-muted-foreground" strokeWidth={1.5} />}
              </>
            )}
            {idea.productName && (
              <>
                <Link to={`/products/${idea.productId}`} className="inline-flex items-center gap-1 hover:text-foreground">
                  <Package className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {idea.productName}
                </Link>
                {idea.projectName && <ChevronRight className="h-3 w-3 text-muted-foreground" strokeWidth={1.5} />}
              </>
            )}
            {idea.projectName && (
              <Link to={`/projects/${idea.projectId}`} className="inline-flex items-center gap-1 hover:text-foreground">
                <FolderKanban className="h-3.5 w-3.5" strokeWidth={1.5} />
                {idea.projectName}
              </Link>
            )}
            {idea.phaseName && (
              <>
                <ChevronRight className="h-3 w-3 text-muted-foreground" strokeWidth={1.5} />
                <span>{idea.phaseName}</span>
              </>
            )}
            {idea.stageName && (
              <>
                <ChevronRight className="h-3 w-3 text-muted-foreground" strokeWidth={1.5} />
                <span>{idea.stageName}</span>
              </>
            )}
            {idea.meetingTitle && (
              <>
                <span className="px-1.5">|</span>
                <Link to={`/meetings/${idea.meetingId}`} className="inline-flex items-center gap-1 hover:text-foreground">
                  <CalendarDays className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {idea.meetingTitle}
                </Link>
              </>
            )}
            {idea.taskTitle && (
              <>
                <span className="px-1.5">|</span>
                <Link to={`/tasks/${idea.taskId}`} className="inline-flex items-center gap-1 hover:text-foreground">
                  <CheckSquare className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {idea.taskCode ?? idea.taskTitle}
                </Link>
              </>
            )}
          </div>

          {/* Tags */}
          <div className="mt-3 flex flex-wrap gap-1.5">
            {editingMeta ? (
              tagOptions.map((opt) => {
                const selected = tagsDraft.includes(opt.value);
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() =>
                      setTagsDraft((prev) =>
                        prev.includes(opt.value)
                          ? prev.filter((v) => v !== opt.value)
                          : [...prev, opt.value],
                      )
                    }
                    style={{ opacity: selected ? 1 : 0.5 }}
                  >
                    <TagBadge value={opt.value} label={opt.label} size="md" />
                  </button>
                );
              })
            ) : idea.tags.length === 0 ? (
              <span className="text-[11px] text-muted-foreground italic">No tags</span>
            ) : (
              idea.tags.map((t) => {
                const opt = tagOptions.find((o) => o.value === t);
                return <TagBadge key={t} value={t} label={opt?.label ?? t} size="md" />;
              })
            )}
          </div>

          {editingMeta && (
            <div className="mt-4 border-t pt-4" style={{ borderColor: BORDER }}>
              <label className="flex items-center gap-2 text-sm mb-4">
                <input
                  type="checkbox"
                  checked={isPinnedDraft}
                  onChange={(e) => setIsPinnedDraft(e.target.checked)}
                />
                Pinned
              </label>
              <AssociationFields
                companyId={companyIdDraft}
                productId={productIdDraft}
                projectId={projectIdDraft}
                meetingId={meetingIdDraft}
                taskId={taskIdDraft}
                showMeeting
                showTask
                onChange={(field, value) => {
                  if (field === "companyId") setCompanyIdDraft(value);
                  else if (field === "productId") setProductIdDraft(value);
                  else if (field === "projectId") setProjectIdDraft(value);
                  else if (field === "meetingId") setMeetingIdDraft(value);
                  else if (field === "taskId") setTaskIdDraft(value);
                }}
              />
            </div>
          )}

          {editingMeta && (
            <div className="mt-4 flex items-center gap-2">
              <Button size="sm" onClick={handleMetaSave} disabled={updateIdea.isPending}>
                <Check className="h-4 w-4" strokeWidth={1.5} />
                Save
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setEditingMeta(false);
                  setTitleDraft(idea.title);
                  setCategoryDraft(idea.category ?? "");
                  setPriorityDraft(idea.priority);
                  setStatusDraft(idea.status);
                  setVisibilityDraft((idea.visibility as "team" | "private") ?? "team");
                  setTagsDraft(idea.tags);
                  setIsPinnedDraft(idea.isPinned);
                  setCompanyIdDraft(idea.companyId ?? null);
                  setProductIdDraft(idea.productId ?? null);
                  setProjectIdDraft(idea.projectId ?? null);
                  setMeetingIdDraft(idea.meetingId ?? null);
                  setTaskIdDraft(idea.taskId ?? null);
                }}
              >
                <X className="h-4 w-4" strokeWidth={1.5} />
                Cancel
              </Button>
            </div>
          )}

          {/* Vote bar */}
          <div
            className="mt-4 flex items-center justify-between border-t pt-4"
            style={{ borderColor: BORDER }}
          >
            <VoteButtons
              votesUp={idea.votesUp}
              votesDown={idea.votesDown}
              userVote={idea.currentUserVote}
              onVote={handleVote}
              disabled={vote.isPending || removeVote.isPending}
              size="md"
            />
            <div className="text-[12px] text-muted-foreground">
              {idea.votesUp} upvotes · {idea.votesDown} downvotes · Net:{" "}
              <span
                style={{
                  color:
                    net > 0
                      ? "hsl(142,65%,30%)"
                      : net < 0
                        ? "hsl(0,70%,38%)"
                        : undefined,
                  fontWeight: 600,
                }}
              >
                {net > 0 ? `+${net}` : net}
              </span>
            </div>
            <div className="text-[12px] text-muted-foreground">
              Your vote:{" "}
              {idea.currentUserVote === "up" ? (
                <span style={{ color: "hsl(142,65%,30%)", fontWeight: 600 }}>▲ Upvoted</span>
              ) : idea.currentUserVote === "down" ? (
                <span style={{ color: "hsl(0,70%,38%)", fontWeight: 600 }}>▼ Downvoted</span>
              ) : (
                <span>Not voted yet</span>
              )}
            </div>
          </div>
        </div>

        {/* ROW 2 — Content */}
        <div
          className="grid gap-4"
          style={{ gridTemplateColumns: headings.length > 0 ? "1fr 200px" : "1fr" }}
        >
          <div
            className="overflow-hidden rounded-xl border bg-white"
            style={{ borderColor: BORDER }}
          >
            <div
              className="flex items-center justify-between border-b px-5 py-3"
              style={{ borderColor: BORDER }}
            >
              <span className="text-[13px] font-semibold">Content</span>
              <div className="flex items-center gap-3">
                {editingContent && (
                  <>
                    <span className="text-[11px] text-muted-foreground">
                      {wordCount} words
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                      {saveState === "saving"
                        ? "Saving..."
                        : saveState === "saved"
                          ? "Saved ✓"
                          : ""}
                    </span>
                    <button
                      type="button"
                      onClick={() => void doContentSave()}
                      className="inline-flex h-7 w-7 items-center justify-center rounded text-[hsl(142,65%,30%)] hover:bg-[hsl(142,55%,94%)]"
                      title="Save"
                    >
                      <Check className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setContentDraft(idea.content ?? "");
                        initialContent.current = idea.content ?? "";
                        setEditingContent(false);
                        setSaveState("idle");
                      }}
                      className="inline-flex h-7 w-7 items-center justify-center rounded text-destructive hover:bg-[hsl(0,70%,96%)]"
                      title="Cancel"
                    >
                      <X className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                  </>
                )}
                {!editingContent && (
                  <button
                    type="button"
                    onClick={() => setEditingContent(true)}
                    className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
                    title="Edit"
                  >
                    <Pencil className="h-4 w-4" strokeWidth={1.5} />
                  </button>
                )}
              </div>
            </div>

            <div className="px-6 py-5">
              {editingContent ? (
                <RichTextEditor
                  content={contentDraft}
                  onChange={setContentDraft}
                  toolbarVariant="full"
                  minHeight={400}
                  placeholder="Start writing..."
                />
              ) : idea.content ? (
                <div
                  className="rich-text-content prose prose-sm max-w-none"
                  style={{ fontSize: 14, lineHeight: 1.8 }}
                  dangerouslySetInnerHTML={{ __html: idea.content }}
                />
              ) : (
                <div className="py-10 text-center text-sm text-muted-foreground italic">
                  No content yet. Click Edit to start writing.
                </div>
              )}
            </div>
          </div>

          {headings.length > 0 && (
            <div
              className="h-fit rounded-xl border bg-white p-4 sticky"
              style={{ borderColor: BORDER, top: 80 }}
            >
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                On this page
              </div>
              <ul className="space-y-1 text-[12px]">
                {headings.map((h) => (
                  <li
                    key={h.id}
                    style={{ paddingLeft: (h.level - 1) * 10 }}
                    className={
                      h.level === 1
                        ? "font-semibold text-foreground"
                        : h.level === 2
                          ? "text-foreground"
                          : "text-muted-foreground"
                    }
                  >
                    <span className="cursor-default truncate block">{h.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* ROW 3 — Tabs */}
        <div
          className="rounded-xl border bg-white"
          style={{ borderColor: BORDER }}
        >
          <div
            className="flex items-center gap-1 border-b px-3 pt-2"
            style={{ borderColor: BORDER }}
          >
            {([
              { key: "decisions", label: "Linked Decisions", count: linkedDecisions.length },
              { key: "tasks", label: "Tasks", count: idea.tasksCount },
              { key: "meetings", label: "Meetings", count: idea.meetingId ? 1 : 0 },
              { key: "comments", label: "Comments", count: comments.length },
              { key: "attachments", label: "Attachments", count: idea.attachmentsCount },
            ] as const).map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-[13px] font-medium transition-colors"
                style={{
                  borderBottom:
                    tab === t.key
                      ? "2px solid hsl(210,100%,50%)"
                      : "2px solid transparent",
                  color: tab === t.key ? "hsl(210,100%,28%)" : "hsl(215,16%,47%)",
                }}
              >
                {t.label}
                <span
                  className="inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-semibold"
                  style={{
                    background: "hsl(210,30%,94%)",
                    color: "hsl(210,30%,40%)",
                  }}
                >
                  {t.count}
                </span>
              </button>
            ))}
          </div>

          <div className="p-5">
            {tab === "decisions" && (
              <div>
                <div className="mb-3 flex items-center justify-between">
                  <div className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Decisions ({linkedDecisions.length})
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setLinkPickerOpen(true)}>
                    <Link2 className="h-4 w-4" strokeWidth={1.5} />
                    Link Decision
                  </Button>
                </div>
                {linkedDecisions.length === 0 ? (
                  <div className="py-8 text-center text-sm text-muted-foreground">
                    No decisions linked yet.
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-lg border" style={{ borderColor: BORDER }}>
                    <table className="w-full text-[13px]" style={{ borderCollapse: "collapse" }}>
                      <thead className="bg-[hsl(210,30%,97%)] text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                        <tr>
                          <th className="px-3 py-2" style={{ width: 110 }}>#</th>
                          <th className="px-3 py-2">Title</th>
                          <th className="px-3 py-2" style={{ width: 110 }}>Status</th>
                          <th className="px-3 py-2" style={{ width: 100 }}>Impact</th>
                          <th className="px-3 py-2" style={{ width: 140 }}>Decided By</th>
                          <th className="px-3 py-2" style={{ width: 110 }}>Date</th>
                          <th className="px-3 py-2 text-right" style={{ width: 80 }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {linkedDecisions.map((d) => (
                          <tr key={d.id}>
                            <td className="px-3 py-2 font-mono text-[12px] text-muted-foreground">
                              {d.code ?? "—"}
                            </td>
                            <td className="px-3 py-2">
                              <Link to={`/decisions/${d.id}`} className="hover:underline">
                                {d.title}
                              </Link>
                            </td>
                            <td className="px-3 py-2 text-muted-foreground">{d.status}</td>
                            <td className="px-3 py-2 text-muted-foreground">{d.impact}</td>
                            <td className="px-3 py-2 text-muted-foreground">
                              {d.decidedByName ?? "—"}
                            </td>
                            <td className="px-3 py-2 text-muted-foreground">
                              {formatDate(d.decidedAt)}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <button
                                type="button"
                                onClick={() => setUnlinkTarget({ id: d.id, title: d.title })}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-[hsl(0,70%,95%)] hover:text-[hsl(0,84%,50%)]"
                                title="Unlink"
                              >
                                <X className="h-[15px] w-[15px]" strokeWidth={1.5} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {tab === "tasks" && (
              <TasksTabContent
                ideaId={idea.id}
                prefill={{
                  companyId: idea.companyId,
                  productId: idea.productId,
                  projectId: idea.projectId,
                  ideaId: idea.id,
                  ideaTitle: idea.title,
                  locked: { ideaId: true },
                }}
              />
            )}

            {tab === "meetings" && (
              <div>
                <div className="mb-3 text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Meeting ({idea.meetingId ? 1 : 0})
                </div>
                {!idea.meetingId ? (
                  <div className="py-8 text-center text-sm text-muted-foreground">
                    Not raised in any meeting. Link one via the Edit form.
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-lg border" style={{ borderColor: BORDER }}>
                    <table className="w-full text-[13px]" style={{ borderCollapse: "collapse" }}>
                      <thead className="bg-[hsl(210,30%,97%)] text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                        <tr>
                          <th className="px-3 py-2" style={{ width: 110 }}>#</th>
                          <th className="px-3 py-2">Title</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        <tr>
                          <td className="px-3 py-2 font-mono text-[12px] text-muted-foreground">
                            {idea.meetingCode ?? "—"}
                          </td>
                          <td className="px-3 py-2">
                            <Link to={`/meetings/${idea.meetingId}`} className="hover:underline">
                              {idea.meetingTitle ?? "Untitled meeting"}
                            </Link>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {tab === "comments" && (
              <div className="space-y-4">
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {comments.length === 0 ? (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                      No comments yet. Start the discussion.
                    </div>
                  ) : (
                    comments.map((c) => (
                      <div key={c.id} className="flex gap-3">
                        <div
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[hsl(210,55%,88%)] text-[11px] font-semibold text-[hsl(210,100%,25%)]"
                        >
                          {(c.authorName ?? "?").charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 text-[12px]">
                            <span className="font-medium">
                              {c.authorName ?? "Unknown"}
                            </span>
                            <span className="text-muted-foreground">
                              {formatRelative(c.createdAt)}
                            </span>
                            {user?.id === c.authorId && (
                              <button
                                type="button"
                                onClick={() => void deleteComment.mutateAsync(c.id)}
                                className="ml-auto text-muted-foreground hover:text-destructive"
                                title="Delete"
                              >
                                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                              </button>
                            )}
                          </div>
                          <div className="mt-1 whitespace-pre-wrap text-[13px]">
                            {c.content}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div
                  className="flex gap-2 border-t pt-4"
                  style={{ borderColor: BORDER }}
                >
                  <textarea
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                        e.preventDefault();
                        void handleAddComment();
                      }
                    }}
                    placeholder="Write a comment... (Ctrl+Enter to submit)"
                    className="flex-1 resize-none rounded-md border px-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-ring"
                    style={{ borderColor: BORDER, minHeight: 60 }}
                  />
                  <Button
                    onClick={() => void handleAddComment()}
                    disabled={!commentText.trim() || addComment.isPending}
                  >
                    <Send className="h-4 w-4" strokeWidth={1.5} />
                    Send
                  </Button>
                </div>
              </div>
            )}

            {tab === "attachments" && (
              <FileUploadZone
                entityType="idea"
                entityId={idea.id}
                entityLabel={idea.code}
                companyId={idea.companyId ?? undefined}
                productId={idea.productId ?? undefined}
                projectId={idea.projectId ?? undefined}
                meetingId={idea.meetingId ?? undefined}
                taskId={idea.taskId ?? undefined}
                showExisting
              />
            )}
          </div>
        </div>

        {/* ROW 4 — Metadata */}
        <div
          className="rounded-xl border bg-white"
          style={{ borderColor: BORDER }}
        >
          <button
            type="button"
            onClick={() => setMetaExpanded((v) => !v)}
            className="flex w-full items-center justify-between px-5 py-3 text-left"
          >
            <span className="text-[13px] font-semibold">Idea Details</span>
            {metaExpanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
            )}
          </button>
          {metaExpanded && (
            <div
              className="space-y-4 border-t p-5"
              style={{ borderColor: BORDER }}
            >
              <div className="grid grid-cols-4 gap-4 text-[13px]">
                <Field label="Submitted By" value={idea.submittedByName ?? "—"} />
                <Field label="Created At" value={new Date(idea.createdAt).toLocaleString()} />
                <Field label="Updated At" value={new Date(idea.updatedAt).toLocaleString()} />
                <Field label="Word Count" value={String(idea.wordCount ?? wordCount)} />
                <Field label="Category" value={categoryLabel ?? "—"} />
                <Field label="Priority" value={priorityLabel} />
                <Field label="Status" value={statusLabel} />
                <Field label="Visibility" value={idea.visibility === "private" ? "Private" : "Team"} />
                <Field label="Code" value={idea.code ?? "—"} />
                <Field label="Votes Up" value={String(idea.votesUp)} />
                <Field label="Votes Down" value={String(idea.votesDown)} />
                <Field label="Net" value={String(idea.votesUp - idea.votesDown)} />
              </div>
              <div className="border-t pt-4" style={{ borderColor: BORDER }}>
                <div className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Linked Context
                </div>
                <div className="grid grid-cols-2 gap-y-2 text-[13px]">
                  <LinkedRow label="Company" value={idea.companyName} href={idea.companyId ? `/companies/${idea.companyId}` : null} />
                  <LinkedRow label="Product" value={idea.productName} href={idea.productId ? `/products/${idea.productId}` : null} />
                  <LinkedRow label="Project" value={idea.projectName} href={idea.projectId ? `/projects/${idea.projectId}` : null} />
                  <LinkedRow label="Phase" value={idea.phaseName} />
                  <LinkedRow label="Stage" value={idea.stageName} />
                  <LinkedRow
                    label="Meeting"
                    value={idea.meetingTitle ? `${idea.meetingCode ?? ""} ${idea.meetingTitle}`.trim() : null}
                    href={idea.meetingId ? `/meetings/${idea.meetingId}` : null}
                  />
                  <LinkedRow
                    label="Task"
                    value={idea.taskTitle ? `${idea.taskCode ?? ""} ${idea.taskTitle}`.trim() : null}
                    href={idea.taskId ? `/tasks/${idea.taskId}` : null}
                  />
                </div>
                <div
                  className="mt-2 flex gap-2 border-t pt-2 text-[13px]"
                  style={{ borderColor: "hsl(210,30%,90%)" }}
                >
                  <span className="w-20 shrink-0 pt-0.5 text-muted-foreground">
                    Decisions:
                  </span>
                  {linkedDecisions.length === 0 ? (
                    <span className="text-muted-foreground">—</span>
                  ) : (
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      {linkedDecisions.map((d) => (
                        <Link
                          key={d.id}
                          to={`/decisions/${d.id}`}
                          className="inline-flex items-center gap-1.5 text-foreground hover:underline"
                        >
                          <span className="font-mono text-[11px] text-muted-foreground">
                            {d.code ?? "—"}
                          </span>
                          <span className="truncate">· {d.title}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {linkPickerOpen && (
        <LinkDecisionPicker
          onClose={() => setLinkPickerOpen(false)}
          excludeIds={linkedDecisions.map((d) => d.id)}
          onPick={async (decisionId) => {
            try {
              await linkDecision.mutateAsync(decisionId);
              showToast("Decision linked");
              setLinkPickerOpen(false);
            } catch (err) {
              showToast(err instanceof Error ? err.message : "Failed to link");
            }
          }}
          token={accessToken}
        />
      )}

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete idea?"
        description={`Are you sure you want to delete '${idea.title}'? It will be removed from your Ideas list.`}
        onConfirm={confirmDelete}
      />

      <ConfirmDialog
        open={unlinkTarget !== null}
        onOpenChange={(open) => {
          if (!open) setUnlinkTarget(null);
        }}
        title="Unlink decision?"
        description={
          unlinkTarget
            ? `Are you sure you want to unlink '${unlinkTarget.title}' from this idea?`
            : ""
        }
        confirmLabel="Unlink"
        onConfirm={async () => {
          if (!unlinkTarget) return;
          try {
            await unlinkDecision.mutateAsync(unlinkTarget.id);
            showToast("Decision unlinked");
          } catch (err) {
            showToast(err instanceof Error ? err.message : "Failed");
            throw err;
          }
        }}
      />
    </AppLayout>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-[13px] text-foreground">{value}</div>
    </div>
  );
}

function LinkedRow({ label, value, href }: { label: string; value: string | null; href?: string | null }) {
  return (
    <div className="flex gap-2">
      <span className="w-20 shrink-0 text-muted-foreground">{label}:</span>
      {value ? (
        href ? (
          <Link to={href} className="text-foreground hover:underline">{value}</Link>
        ) : (
          <span className="text-foreground">{value}</span>
        )
      ) : (
        <span className="text-muted-foreground">—</span>
      )}
    </div>
  );
}

function LinkDecisionPicker({
  onClose,
  onPick,
  excludeIds,
  token,
}: {
  onClose: () => void;
  onPick: (decisionId: string) => void | Promise<void>;
  excludeIds: string[];
  token: string | null;
}) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState<DecisionLite[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const res = await apiFetch<{ data: DecisionLite[] }>(`/decisions`, {
          token,
          query: { page: 1, limit: 50, search: q || undefined },
        });
        if (!cancelled) setItems(res.data ?? []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [q, token]);

  const filtered = items.filter((d) => !excludeIds.includes(d.id));

  return (
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/30"
      onClick={onClose}
    >
      <div
        className="w-[560px] max-h-[80vh] rounded-xl border bg-white shadow-xl"
        style={{ borderColor: BORDER }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center justify-between border-b px-5 py-3"
          style={{ borderColor: BORDER }}
        >
          <div className="flex items-center gap-2">
            <Link2 className="h-4 w-4" strokeWidth={1.5} />
            <span className="text-[14px] font-semibold">Link Decision</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>
        <div className="p-4">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              strokeWidth={1.5}
            />
            <Input
              placeholder="Search decisions..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="mt-3 max-h-[400px] overflow-y-auto rounded-md border" style={{ borderColor: BORDER }}>
            {loading ? (
              <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
                Loading…
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                <FileText className="mx-auto h-8 w-8 text-muted-foreground" strokeWidth={1.5} />
                <div className="mt-2">No decisions found</div>
              </div>
            ) : (
              <ul className="divide-y" style={{ borderColor: BORDER }}>
                {filtered.map((d) => (
                  <li key={d.id}>
                    <button
                      type="button"
                      onClick={() => void onPick(d.id)}
                      className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-[hsl(210,50%,98%)]"
                    >
                      <span className="font-mono text-[11px] text-muted-foreground">
                        {d.code ?? "—"}
                      </span>
                      <span className="flex-1 text-[13px] text-foreground">{d.title}</span>
                      <span className="text-[11px] text-muted-foreground">{d.status}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
